import { useState, useEffect } from 'react'
import { Play } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Skeleton } from '@/components/ui'
import { useRedirect } from '@/contexts/redirect-context'
import { redirectToVideo } from '@/utils/redirect'

interface VideoItem {
  id: string
  title: string
  thumbnail: string
  videoUrl: string
  dimensions?: { width: number; height: number }
}

export function HomePage() {
  return (
    <div className="py-12 space-y-16">
      {/* Hero + CTA */}
      <div className="flex flex-col items-center px-8 max-w-5xl mx-auto">
        <Hero />
        <div className="mt-10">
          <button
            onClick={() => redirectToVideo('i2v')}
            className="inline-flex items-center gap-3 bg-white hover:bg-gray-200 text-black text-lg font-bold px-8 py-4 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:shadow-[0_0_50px_rgba(255,255,255,0.4)] hover:scale-105"
          >
            Get Started
          </button>
        </div>
      </div>

      {/* Community Gallery */}
      <div className="w-[80%] mx-auto">
        <CommunityGallery />
      </div>
    </div>
  )
}

function Hero() {
  return (
    <div className="text-center max-w-2xl">
      <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
        Bring Your Ideas to Life
      </h1>
      <p className="mt-4 text-lg text-[#888]">
        Describe the motion and watch the magic happen.
      </p>
    </div>
  )
}

function CommunityGallery() {
  const [videos, setVideos] = useState<VideoItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { openRedirect } = useRedirect()

  useEffect(() => {
    fetch('/videos.json')
      .then((res) => res.json())
      .then((data) => setVideos(data.videos || []))
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }, [])

  const handleVideoClick = () => {
    openRedirect()
  }

  return (
    <section>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-white">
          See What Others Have Created
        </h2>
        <p className="text-[#666] mt-1">
          Get inspired by the community's creations
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[9/16] rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {videos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onClick={handleVideoClick}
            />
          ))}
        </div>
      )}
    </section>
  )
}

interface VideoCardProps {
  video: VideoItem
  onClick: () => void
}

function VideoCard({ video, onClick }: VideoCardProps) {
  const [isLoaded, setIsLoaded] = useState(false)

  return (
    <div
      className="group relative aspect-[9/16] rounded-xl overflow-hidden border border-white/[0.08] cursor-pointer"
      onClick={onClick}
    >
      {/* Loading skeleton */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-white/[0.03] animate-pulse" />
      )}

      {/* Thumbnail */}
      <img
        src={video.thumbnail}
        alt={video.title}
        onLoad={() => setIsLoaded(true)}
        className={cn(
          'w-full h-full object-cover transition-all duration-300 group-hover:scale-105',
          isLoaded ? 'opacity-100' : 'opacity-0'
        )}
      />

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
          <Play className="size-6 text-white fill-white" />
        </div>
      </div>

      {/* Title */}
      <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-sm font-medium text-white truncate">{video.title}</p>
      </div>
    </div>
  )
}
